import type { Goal, Sex } from "@bioflow/contracts";
import { Check } from "lucide-react";
import { GOAL_ICONS } from "./Icons";
import { params as text } from "../content/onboarding";
import { GOALS, GOAL_LABELS, HEIGHT_RANGE, SEX_LABELS, WEIGHT_RANGE, calculateBmi, formatBmi } from "../lib/profile";
import { haptic } from "../lib/telegram";

/** Список целей — экран 5 онбординга и «Редактировать» в профиле. */
export function GoalPicker({ value, onChange }: { value: Goal | null; onChange: (goal: Goal) => void }) {
  return (
    <div className="stack">
      {GOALS.map((goal) => {
        const Icon = GOAL_ICONS[goal];
        const active = value === goal;
        return (
          <button
            key={goal}
            type="button"
            className={`choice choice--goal${active ? " choice--active" : ""}`}
            onClick={() => {
              haptic();
              onChange(goal);
            }}
            aria-pressed={active}
          >
            <span className="choice__icon">
              <Icon size={18} strokeWidth={1.9} aria-hidden />
            </span>
            <span className="choice__label">{GOAL_LABELS[goal]}</span>
            {active && <Check size={18} strokeWidth={2.4} aria-hidden className="choice__check" />}
          </button>
        );
      })}
    </div>
  );
}

export type BodyValues = { sex: Sex | null; heightCm: string; weightKg: string };

export const EMPTY_BODY: BodyValues = { sex: null, heightCm: "", weightKg: "" };

/** Разбор полей роста и веса; null — поле пустое или вне допустимых пределов. */
export function parseBody(values: BodyValues): { heightCm: number | null; weightKg: number | null } {
  const height = Number(values.heightCm.replace(",", "."));
  const weight = Number(values.weightKg.replace(",", "."));
  const inRange = (n: number, range: { min: number; max: number }) => Number.isFinite(n) && n >= range.min && n <= range.max;
  return {
    heightCm: values.heightCm && inRange(height, HEIGHT_RANGE) ? Math.round(height) : null,
    weightKg: values.weightKg && inRange(weight, WEIGHT_RANGE) ? Math.round(weight * 10) / 10 : null,
  };
}

/** Пол, рост, вес и живой расчёт BMI — экран 6 онбординга и редактирование. */
export function BodyFields({ values, onChange }: { values: BodyValues; onChange: (patch: Partial<BodyValues>) => void }) {
  const parsed = parseBody(values);
  const bmi = parsed.heightCm !== null && parsed.weightKg !== null ? calculateBmi(parsed.heightCm, parsed.weightKg) : null;

  return (
    <>
      <div className="field">
        <div className="field__label">{text.sex}</div>
        <div className="segmented" role="radiogroup" aria-label={text.sex}>
          {(Object.keys(SEX_LABELS) as Sex[]).map((sex) => (
            <button
              key={sex}
              type="button"
              role="radio"
              aria-checked={values.sex === sex}
              className={`segmented__item${values.sex === sex ? " segmented__item--active" : ""}`}
              onClick={() => {
                haptic();
                onChange({ sex });
              }}
            >
              {SEX_LABELS[sex]}
            </button>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label className="field__label" htmlFor="heightCm">{text.height}</label>
          <div className="unit-input">
            <input
              id="heightCm"
              inputMode="numeric"
              placeholder="180"
              value={values.heightCm}
              onChange={(event) => onChange({ heightCm: event.target.value.replace(/[^\d]/g, "").slice(0, 3) })}
              aria-invalid={values.heightCm && parsed.heightCm === null ? "true" : undefined}
            />
            <span>{text.heightUnit}</span>
          </div>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="weightKg">{text.weight}</label>
          <div className="unit-input">
            <input
              id="weightKg"
              inputMode="decimal"
              placeholder="92"
              value={values.weightKg}
              onChange={(event) => onChange({ weightKg: event.target.value.replace(/[^\d.,]/g, "").slice(0, 5) })}
              aria-invalid={values.weightKg && parsed.weightKg === null ? "true" : undefined}
            />
            <span>{text.weightUnit}</span>
          </div>
        </div>
      </div>

      <div className={`bmi-preview${bmi === null ? " bmi-preview--empty" : ""}`}>
        <span>{text.bmiPreview}</span>
        <strong>{bmi === null ? "—" : formatBmi(bmi)}</strong>
      </div>
    </>
  );
}
